import { useMemo } from 'react'
import projects from '@/content/projects.json'
import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { slugify } from '@/lib/slug'
import StatusPill, { type ItemStatus } from '@/components/ui/StatusPill'

type Project = {
  slug?: string
  title: string
  desc: string
  tech?: string[]
  repo?: string
  demo?: string
  cover?: string
  cover3d?: string
  tags?: string[]
  preview?: boolean
  status?: ItemStatus
}

type Props = {
  onPreview?: (project: Project) => void
  variant?: 'default' | 'lite'
  layout?: 'carousel' | 'grid'
}

function toPublicUrl(path?: string) {
  if (!path) return ''
  const p = path.replace(/^\/?public\//, '/')
  return p.startsWith('/') ? p : `/${p}`
}

export default function ProjectsCarousel({ onPreview, variant = 'default', layout = 'carousel' }: Props) {
  const data = useMemo<Project[]>(() => projects as Project[], [])
  const isLite = variant === 'lite'
  const isGrid = layout === 'grid'
  const location = useLocation()

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className={isLite ? 'text-2xl font-extrabold italic tracking-tight text-slate-100' : 'text-xl font-semibold'}>
          Projets
        </h2>
      </div>

      <div className={isGrid ? '' : 'overflow-x-auto pb-2'}>
        <div
          className={
            isGrid
              ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
              : 'flex gap-4 min-w-full'
          }
        >
          {data.map((p, i) => (
            <motion.article
              key={p.title + i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              whileHover={isLite ? { y: -2 } : undefined}
              className={
                isLite
                  ? `${isGrid ? '' : 'w-80 shrink-0'} lite-card group rounded-2xl overflow-hidden`
                  : 'w-80 shrink-0 rounded-lg border border-slate-800 bg-slate-900/40'
              }
            >
              <div
                className={
                  isLite
                    ? 'aspect-video w-full relative overflow-hidden bg-gradient-to-br from-slate-950/35 via-slate-950/15 to-slate-900/25'
                    : 'aspect-video w-full relative overflow-hidden bg-gradient-to-br from-slate-900/70 via-slate-900/35 to-slate-950/40'
                }
              >
                {p.cover ? (
                  <img
                    src={toPublicUrl(p.cover)}
                    alt={p.title}
                    loading="lazy"
                    decoding="async"
                    className={
                      isLite
                        ? 'h-full w-full object-contain lite-media'
                        : 'h-full w-full object-contain'
                    }
                  />
                ) : (
                  // <div className={isLite ? 'h-full w-full bg-slate-950/25' : 'h-full w-full bg-slate-800/40'} />
                  <img src={toPublicUrl('public/assets/covers/default_cover.png')} 
                  alt="Default project cover" loading="lazy" decoding="async" className="h-full w-full object-contain" />
                )}
                <div className="absolute left-3 top-3">
                  <StatusPill status={p.status} variant={variant} />
                </div>
              </div>

              <div className={isLite ? 'p-5 space-y-2' : 'p-4 space-y-2'}>
                <div className={isLite ? 'text-lg font-semibold text-slate-100' : 'text-base font-medium'}>
                  {p.title}
                </div>
                <p className={isLite ? 'text-sm text-slate-300 leading-relaxed' : 'text-sm text-slate-300 line-clamp-3'}>
                  {p.desc}
                </p>
                {p.tech && p.tech.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {p.tech.map((t) => (
                      <span
                        key={t}
                        className={
                          isLite
                            ? 'rounded-xl border border-slate-700 bg-slate-950/25 px-2 py-0.5 text-xs text-slate-300'
                            : 'rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-300'
                        }
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2">
                  {p.repo && (
                    <a
                      href={p.repo}
                      target="_blank"
                      rel="noreferrer"
                      className={
                        isLite
                          ? 'text-xs font-semibold text-brand-300 hover:underline'
                          : 'text-xs text-brand-400 hover:underline'
                      }
                    >
                      Code
                    </a>
                  )}
                  {p.demo && (
                    <a
                      href={p.demo}
                      target="_blank"
                      rel="noreferrer"
                      className={
                        isLite
                          ? 'text-xs font-semibold text-brand-300 hover:underline'
                          : 'text-xs text-brand-400 hover:underline'
                      }
                    >
                      Demo
                    </a>
                  )}
                  <Link
                    to={`/portfolio/project/${p.slug ?? slugify(p.title)}`}
                    state={{ backgroundLocation: location, variant }}
                    className={
                      isLite
                        ? 'text-xs font-semibold text-slate-200 hover:underline'
                        : 'text-xs text-brand-400 hover:underline'
                    }
                  >
                    Détails
                  </Link>
                  {p.preview && p.demo && (
                    <button
                      type="button"
                      onClick={() => onPreview?.(p)}
                      className={
                        isLite
                          ? 'ml-auto rounded-xl border border-slate-700 bg-slate-950/25 px-3 py-1 text-xs font-semibold text-slate-100 hover:bg-slate-950/45 transition-colors'
                          : 'ml-auto rounded border border-brand-500 px-2 py-1 text-xs text-brand-300 hover:bg-brand-500/10 hover:text-brand-200 transition-colors'
                      }
                    >
                      Preview
                    </button>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  )
}


